/**
 * Launcher payloads — firing a carried grenade out of a launcher.
 *
 * A launcher has no statistics of its own: every one in the catalogue prints
 * `Variable` for damage, penetration and clip, because what it does is entirely
 * determined by the round loaded into it. The grenade supplies the damage, the
 * penetration and the qualities (including `Blast (X)`, which the rules engine turns
 * into an `areaEffect` and so into a reported blast radius).
 *
 * This reuses the existing loaded-ammunition seam rather than adding a parallel one:
 * `weapon.loadedAmmoId` holds the payload's sourceId, and `EquipmentModifierService`
 * resolves it. `AmmunitionService` handles the nine unusual ammunition types from RT
 * Table 5-11, which *modify* a weapon's own damage; a payload *replaces* it, so the
 * two are resolved separately and a launcher only ever takes a payload.
 *
 * Stock: a grenade is a CONSUMABLE. Buying three gives one weapon entry holding three,
 * not three entries, so `clip` is how many you own and `currentClip` how many are left
 * — it reads "2/3" and the number comes from the inventory rather than a fixed loadout.
 * Firing a launcher consumes one from the loaded grenade's stock, exactly as throwing it
 * by hand does.
 */

import type { Character, Weapon } from '../types/character';
import { gameDataService } from './GameDataService';

/**
 * One purchased grenade is one grenade.
 *
 * The stack size comes from how many the character actually owns, not from a loadout
 * constant: buying three frag grenades yields a single entry holding 3. Grenades are
 * restocked from ship's stores between engagements, which is a purchase, not a reload.
 */
export const SINGLE_GRENADE = 1;

const LAUNCHER_SUBTYPE = 'Launcher';
const PAYLOAD_SUBTYPE = 'Grenades & Explosives';

/**
 * Subtypes whose items are expended on use.
 *
 * Grenades are thrown or launched; narcotics are taken for a buff. Both are spent, so
 * both want a quantity that depletes rather than one inventory entry per unit. Deliberately
 * broader than `isPayload`: every payload is a consumable, but a narcotic is a consumable
 * that cannot be loaded into a launcher.
 */
const CONSUMABLE_SUBTYPES = new Set([PAYLOAD_SUBTYPE, 'Drugs & Consumables']);

export interface LauncherPayload {
  /** items.json id of the grenade. */
  sourceId: string;
  name: string;
  /** Dice formula, or '-'/'Special' when the grenade deals no rollable damage. */
  damage: string;
  damageType?: string;
  penetration: number;
  special: string[];
  /**
   * False when the catalogue gives this grenade no penetration at all. Three do not
   * (haywire-grenade, psykotroke-grenade, web); they launch at 0 until the figures are
   * re-derived from the book tables. Surfaced so the gap is visible rather than
   * silently becoming a real zero.
   */
  penetrationKnown: boolean;
}

/** A payload the character is carrying, with how many are left. */
export interface AvailablePayload {
  payload: LauncherPayload;
  /** The character's weapon entry that holds the stock. */
  weaponId: string;
  remaining: number;
  carried: number;
}

const subtypeOf = (sourceId: string): string | undefined =>
  (gameDataService.getShopItemByStringId(sourceId) as { subtype?: string } | undefined)?.subtype;

/** True when this weapon fires a loaded payload rather than having its own damage. */
export function isLauncher(weapon: Pick<Weapon, 'sourceId'>): boolean {
  return subtypeOf(weapon.sourceId) === LAUNCHER_SUBTYPE;
}

/** True when this item can be loaded into a launcher. */
export function isPayload(sourceId: string): boolean {
  return subtypeOf(sourceId) === PAYLOAD_SUBTYPE;
}

/** Resolve a payload's statistics from the catalogue, or null if it is not one. */
export function getPayload(sourceId: string): LauncherPayload | null {
  if (!isPayload(sourceId)) return null;
  const item = gameDataService.getShopItemByStringId(sourceId) as
    | {
        name?: string;
        weaponStats?: {
          damage?: string;
          damageType?: string;
          penetration?: number | string;
          special?: string | string[];
        };
      }
    | undefined;
  const ws = item?.weaponStats;
  if (!ws) return null;

  const special = !ws.special ? [] : Array.isArray(ws.special) ? ws.special : [ws.special];
  const pen = typeof ws.penetration === 'number' ? ws.penetration : undefined;

  return {
    sourceId,
    name: item?.name ?? sourceId,
    damage: ws.damage ?? '-',
    damageType: ws.damageType,
    penetration: pen ?? 0,
    penetrationKnown: pen !== undefined,
    special
  };
}

/** How many of a carried grenade remain. `clip` is how many are owned. */
export function stockOf(weapon: Weapon): { remaining: number; carried: number } {
  const carried = typeof weapon.clip === 'number' ? weapon.clip : SINGLE_GRENADE;
  const remaining = typeof weapon.currentClip === 'number' ? weapon.currentClip : carried;
  return { remaining, carried };
}

/**
 * Every grenade the character is carrying that could be loaded into a launcher.
 *
 * Deliberately not filtered by launcher model: any grenade fits any launcher. The
 * per-ammunition `compatibleWeapons` lists that `AmmunitionService` uses would need
 * 47 grenades x 18 launchers to say the same thing.
 */
export function getAvailablePayloads(character: Character): AvailablePayload[] {
  return (character.weapons ?? [])
    .map((w) => {
      const payload = getPayload(w.sourceId);
      if (!payload) return null;
      const { remaining, carried } = stockOf(w);
      return { payload, weaponId: w.id, remaining, carried };
    })
    .filter((p): p is AvailablePayload => p !== null);
}

/** The character's weapon entry holding stock of a payload, if any remains. */
export function findPayloadStock(
  character: Character,
  sourceId: string
): AvailablePayload | undefined {
  return getAvailablePayloads(character).find(
    (p) => p.payload.sourceId === sourceId && p.remaining > 0
  );
}

/**
 * True when a damage string can actually be rolled.
 *
 * Launchers print `Variable` and several grenades print `-` or `Special`. None is a
 * dice formula, and `DiceService.rollWeaponDamage` falls back to 1d10 when its regex
 * fails to match — so an unguarded launcher silently rolled 1d10 whatever was loaded.
 */
export function isRollableDamage(damage: string | undefined): boolean {
  return !!damage && /\d+d\d+/.test(damage);
}

/**
 * True when the item is expended on use and so should be tracked as a depleting
 * quantity rather than as one inventory entry per unit.
 *
 * Covers grenades (thrown or launched) and narcotics (taken for a buff). Distinct from
 * `isAmmunition`, which routes rounds into `ammoInventory` and never appears in the
 * character's own equipment: a consumable is a real item you hold, that runs out.
 *
 * A grenade is a consumable weapon and stacks on `weapon.clip`; a narcotic is consumable
 * gear and stacks on `equipment.quantity`. `isPayload` remains the narrower test for
 * "can be loaded into a launcher".
 */
export function isConsumable(sourceId: string): boolean {
  const subtype = subtypeOf(sourceId);
  return !!subtype && CONSUMABLE_SUBTYPES.has(subtype);
}
